/**
 * The invite ticket's three fields, and what a refused submission means.
 *
 * ── What the endpoint actually does, since the copy above it depends on knowing ──────────────
 * `POST /customer/invite` really does invite. `CustomerOnboardingProcessor.InviteAsync` opens a
 * roster entry with status `Invited`, begins the coaching relation and issues an offer that expires
 * in 14 days (`InvitationLifetime`); nothing is activated until the person accepts. It answers with
 * a `GetCustomerResponse` rather than an invitation DTO, and that response carries no invitation
 * CODE — the ticket flow KANON 02 drew a `8FQ2-KD` for has no field to read, so it is not drawn
 * rather than filled with a placeholder.
 *
 * It branches on who holds the address, and `hasOpenEngagement` is the one field that says which
 * branch ran. `model/sentInvitation` reads it and owns everything that follows from it.
 *
 * The validation below is the client-side half only. The backend's own rules are `email` non-empty
 * and each name at most 50 characters (`PostInviteCustomerRequest`), and it is the authority on
 * whether an address can be invited at all — which is why the three checks below are REFUSAL
 * mappings and not rules.
 *
 * Pure: no React, no DOM, no query hook, no i18next. It returns KEYS.
 */

export interface InviteDraft {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
}

/**
 * The order the three fields are asked in, and therefore the order they are drawn in — the form
 * reads this rather than repeating it, so "the first field the trainer has to fix" cannot come to
 * mean a different field from the first one on screen.
 */
export const INVITE_FIELD_ORDER: readonly (keyof InviteDraft)[] = [
  'firstName',
  'lastName',
  'email',
];

export const EMPTY_INVITE_DRAFT: InviteDraft = { firstName: '', lastName: '', email: '' };

/** A field's complaint, as an i18n key. `null` means the field is fine. */
export interface InviteErrors {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly email: string | null;
}

export const NO_INVITE_ERRORS: InviteErrors = { firstName: null, lastName: null, email: null };

/**
 * The backend's own cap, mirrored so a 51st character is refused where the trainer can see it
 * rather than after a round trip.
 */
const MAX_NAME_LENGTH = 50;

/**
 * Deliberately loose: one `@`, something either side, a dot in the domain.
 *
 * A stricter pattern rejects addresses that exist — `+` tags, new TLDs, quoted locals — and the
 * server is the only authority on deliverability anyway. This exists to catch a typo before a round
 * trip, not to be a specification.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export function validateInvite(draft: InviteDraft): InviteErrors {
  return {
    firstName: nameError(draft.firstName),
    lastName: nameError(draft.lastName),
    email:
      draft.email.trim() === ''
        ? 'web:clients.inviteDialog.required'
        : EMAIL_SHAPE.test(draft.email.trim())
          ? null
          : 'web:clients.inviteDialog.emailInvalid',
  };
}

/**
 * Two refusals, not one. A name past the column length was pointing at "Uzupełnij to pole." over a
 * field the trainer had visibly filled — which reads as the form having lost what they typed, and
 * leaves them with no idea what to change.
 */
function nameError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return 'web:clients.inviteDialog.required';
  return trimmed.length > MAX_NAME_LENGTH ? 'web:clients.inviteDialog.tooLong' : null;
}

/**
 * The field the trainer has to fix first, or `null` when the form is fine.
 *
 * Both the gate on submitting and the answer to "where does focus go now". A refused submission is
 * otherwise silent to anyone not looking at the form: three messages appear inline, the button they
 * pressed keeps its name and its focus, and nothing says the press did anything at all (SC 3.3.1).
 * Moving focus to this field is what delivers the refusal — `aria-invalid` and `aria-describedby`
 * are already on it, so the reason is read out with it.
 *
 * Topmost-first, in `INVITE_FIELD_ORDER`, because that is the order a person reads the form in.
 */
export function firstInviteError(errors: InviteErrors): keyof InviteDraft | null {
  return INVITE_FIELD_ORDER.find((field) => errors[field] !== null) ?? null;
}

/** The request body, trimmed. The backend stores what it is sent. */
export function inviteRequest(draft: InviteDraft): {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
} {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    email: draft.email.trim(),
  };
}

/**
 * `duplicate_email` — `Domain/ValidationErrorCodes.cs`. Three different situations raise it: a
 * pending invitation for the same address, an address that is already this trainer's client, and an
 * address registered to any account at all. The trainer can act on all three the same way, so they
 * share one sentence — the mobile app's, verbatim.
 */
const DUPLICATE_EMAIL = 'duplicate_email';

/** `seat_limit` — the same file. A 400, not a 402 or a 403. */
const SEAT_LIMIT = 'seat_limit';

/**
 * `invitee_is_trainer` — the same file, raised by `OfferToExistingAccountAsync` when the address
 * belongs to somebody with a TRAINER account.
 *
 * It belongs on the field for the same reason `duplicate_email` does: the address is the whole
 * problem and retyping it is the whole fix. Said at the foot of the form instead, it read as "the
 * invitation failed" over three fields the trainer had visibly filled in correctly.
 */
const INVITEE_IS_TRAINER = 'invitee_is_trainer';

/**
 * Converting a public-form lead whose address already has an account (public-form, the conversion
 * section of the plan): `409 lead_email_taken`. The trainer invites normally instead, so the
 * refusal belongs on the address field like the duplicate above.
 */
const LEAD_EMAIL_TAKEN = 'lead_email_taken';

/**
 * Converting a lead somebody already converted — another tab, another device: `409
 * lead_already_converted`. Nothing on the form is wrong, so it is said at the foot of it; the host
 * reads the lead again, and its page then shows the conversion.
 */
const LEAD_ALREADY_CONVERTED = 'lead_already_converted';

export type InviteRefusal =
  /** The address is taken. Belongs on the e-mail field, not on the form. */
  | { readonly kind: 'field'; readonly field: 'email'; readonly messageKey: string }
  /** Every seat is spoken for. The ticket turns into the limit panel. */
  | { readonly kind: 'seatLimit' }
  /** Anything else, said once at the foot of the form. */
  | { readonly kind: 'form'; readonly messageKey: string };

export function inviteRefusal(status: number, code: string | null | undefined): InviteRefusal {
  if (status === 400 && code === DUPLICATE_EMAIL) {
    return {
      kind: 'field',
      field: 'email',
      // The mobile app's own sentence, so a trainer who has met this on the phone reads the same
      // words here. KANON's sample copy words it differently; parity wins over a mockup's filler.
      messageKey: 'clients.invite.errors.duplicateEmail',
    };
  }
  if (status === 400 && code === INVITEE_IS_TRAINER) {
    return {
      kind: 'field',
      field: 'email',
      // The mobile app's own sentence again, so the two clients refuse this address identically.
      messageKey: 'clients.invite.errors.inviteeIsTrainer',
    };
  }
  if (status === 409 && code === LEAD_EMAIL_TAKEN) {
    return { kind: 'field', field: 'email', messageKey: 'web:publicForm.leads.convert.emailTaken' };
  }
  if (status === 409 && code === LEAD_ALREADY_CONVERTED) {
    return { kind: 'form', messageKey: 'web:publicForm.leads.convert.alreadyConverted' };
  }
  if (status === 400 && code === SEAT_LIMIT) return { kind: 'seatLimit' };
  return { kind: 'form', messageKey: 'clients.invite.errors.generic' };
}
